import { getAuthToken } from '@services/api/auth.api'
import { getAllClinics } from '@services/api/clinic.api'

const KIOSK_DB_NAME_KEY = 'kiosk_db_name'
const KIOSK_CLINIC_ID_KEY = 'kiosk_clinic_id'
const KIOSK_DEVICE_MODE_KEY = 'kiosk_device_mode'
const KIOSK_ACTIVATION_TOKEN_KEY = 'kiosk_activation_token'
const KIOSK_ACTIVATION_ACTOR_KEY = 'kiosk_activation_actor_id'
const KIOSK_PATIENT_TOKEN_KEY = 'kiosk_patient_token'
const KIOSK_PATIENT_ID_KEY = 'kiosk_patient_id'
const KIOSK_PATIENT_MODE_KEY = 'kiosk_patient_mode'

export interface KioskClinicBinding {
  dbName: string
  clinicId: string
}

export interface KioskActivationResult {
  binding: KioskClinicBinding
  accessToken: string
  actorId: string
}

export interface KioskActivationSession {
  accessToken: string
  actorId: string
}

export interface KioskPatientSession {
  accessToken: string
  patientId: string
  identificationMode: 'existing' | 'new'
}

/**
 * Restore the tab-scoped staff grant used for new-patient registration.
 * sessionStorage survives refresh without sharing the grant with other tabs.
 */
export function getKioskActivationSession(): KioskActivationSession | null {
  if (typeof sessionStorage === 'undefined') return null
  const accessToken =
    sessionStorage.getItem(KIOSK_ACTIVATION_TOKEN_KEY)?.trim() ?? ''
  const actorId =
    sessionStorage.getItem(KIOSK_ACTIVATION_ACTOR_KEY)?.trim() ?? ''
  if (accessToken === '' || actorId === '') return null
  return { accessToken, actorId }
}

export function setKioskActivationSession(
  session: KioskActivationSession,
): void {
  const accessToken = session.accessToken.trim()
  const actorId = session.actorId.trim()
  if (accessToken === '' || actorId === '') {
    throw new Error('A valid kiosk activation session is required')
  }
  sessionStorage.setItem(KIOSK_ACTIVATION_TOKEN_KEY, accessToken)
  sessionStorage.setItem(KIOSK_ACTIVATION_ACTOR_KEY, actorId)
}

export function clearKioskActivationSession(): void {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(KIOSK_ACTIVATION_TOKEN_KEY)
  sessionStorage.removeItem(KIOSK_ACTIVATION_ACTOR_KEY)
}

/** Active patient grant, scoped to this kiosk tab and cleared on start-over. */
export function getKioskPatientSession(): KioskPatientSession | null {
  if (typeof sessionStorage === 'undefined') return null
  const accessToken =
    sessionStorage.getItem(KIOSK_PATIENT_TOKEN_KEY)?.trim() ?? ''
  const patientId = sessionStorage.getItem(KIOSK_PATIENT_ID_KEY)?.trim() ?? ''
  const identificationMode = sessionStorage.getItem(KIOSK_PATIENT_MODE_KEY)
  if (
    accessToken === '' ||
    patientId === '' ||
    (identificationMode !== 'existing' && identificationMode !== 'new')
  ) {
    return null
  }
  return { accessToken, patientId, identificationMode }
}

export function setKioskPatientSession(session: KioskPatientSession): void {
  const accessToken = session.accessToken.trim()
  const patientId = session.patientId.trim()
  if (accessToken === '' || patientId === '') {
    throw new Error('A valid kiosk patient session is required')
  }
  sessionStorage.setItem(KIOSK_PATIENT_TOKEN_KEY, accessToken)
  sessionStorage.setItem(KIOSK_PATIENT_ID_KEY, patientId)
  sessionStorage.setItem(KIOSK_PATIENT_MODE_KEY, session.identificationMode)
}

export function clearKioskPatientSession(): void {
  if (typeof sessionStorage === 'undefined') return
  sessionStorage.removeItem(KIOSK_PATIENT_TOKEN_KEY)
  sessionStorage.removeItem(KIOSK_PATIENT_ID_KEY)
  sessionStorage.removeItem(KIOSK_PATIENT_MODE_KEY)
}

export interface KioskClinicOption {
  clinicId: string
  clinicName: string
}

export interface KioskClinicSelectionRequired {
  requiresClinicSelection: true
  dbName: string
  accessToken: string
  actorId: string
  clinics: KioskClinicOption[]
  suggestedClinicId: string
}

export type KioskActivationOutcome =
  | KioskActivationResult
  | KioskClinicSelectionRequired

/** Plain kiosk URL — clinic context comes from device binding, not query params. */
export function buildKioskFormsPath(): string {
  return '/forms/kiosk'
}

export function getKioskBinding(): KioskClinicBinding | null {
  const dbName = localStorage.getItem(KIOSK_DB_NAME_KEY)?.trim() ?? ''
  if (dbName === '') return null
  const clinicId = localStorage.getItem(KIOSK_CLINIC_ID_KEY)?.trim() ?? ''
  return { dbName, clinicId }
}

export function setKioskBinding(binding: KioskClinicBinding): void {
  const dbName = binding.dbName.trim()
  if (dbName === '') {
    throw new Error('dbName is required to activate kiosk')
  }
  localStorage.setItem(KIOSK_DB_NAME_KEY, dbName)
  const clinicId = binding.clinicId.trim()
  if (clinicId !== '') {
    localStorage.setItem(KIOSK_CLINIC_ID_KEY, clinicId)
  } else {
    localStorage.removeItem(KIOSK_CLINIC_ID_KEY)
  }
}

export function clearKioskBinding(): void {
  localStorage.removeItem(KIOSK_DB_NAME_KEY)
  localStorage.removeItem(KIOSK_CLINIC_ID_KEY)
}

/** Drop device lock and clinic binding. Does not call clinic-app logout. */
export function clearKioskDeviceSession(): void {
  sessionStorage.removeItem(KIOSK_DEVICE_MODE_KEY)
  clearKioskActivationSession()
  clearKioskPatientSession()
  // Remove the legacy browser-wide lock so existing installations recover.
  localStorage.removeItem(KIOSK_DEVICE_MODE_KEY)
  clearKioskBinding()
}

/** This tab was activated as a patient kiosk and must not open the clinic app. */
export function isKioskDeviceMode(): boolean {
  if (typeof sessionStorage === 'undefined') return false
  return sessionStorage.getItem(KIOSK_DEVICE_MODE_KEY) === '1'
}

export function setKioskDeviceMode(): void {
  sessionStorage.setItem(KIOSK_DEVICE_MODE_KEY, '1')
  // Clean up the old browser-wide marker written by previous versions.
  localStorage.removeItem(KIOSK_DEVICE_MODE_KEY)
}

/** Copy current staff session clinic into kiosk binding (same browser profile). */
export function activateKioskBindingFromStaffSession(): boolean {
  const dbName = localStorage.getItem('db_name')?.trim() ?? ''
  if (dbName === '') return false
  setKioskBinding({
    dbName,
    clinicId: localStorage.getItem('clinic_id')?.trim() ?? '',
  })
  return true
}

/** Open shared kiosk; activates binding from staff session when available. */
export function openKioskWindow(): void {
  activateKioskBindingFromStaffSession()
  window.open(
    buildKioskFormsPath(),
    'kiosk-form',
    'width=785,height=900,scrollbars=yes',
  )
}

/** Resolve clinic for OTP/forms from device binding only. */
export function resolveKioskClinicContext(): KioskClinicBinding {
  const binding = getKioskBinding()
  if (binding != null) return binding
  return { dbName: '', clinicId: '' }
}

const STAFF_LOGIN_SCOPE =
  'read:admin write:admin read:provider write:provider read:patient write:patient'

export function completeKioskActivation(params: {
  dbName: string
  accessToken: string
  actorId: string
  clinics: KioskClinicOption[]
  clinicId: string
}): KioskActivationResult {
  const selectedClinic = params.clinics.find(
    (clinic) => clinic.clinicId === params.clinicId,
  )
  if (!selectedClinic) throw new Error('Select a valid clinic.')
  const binding: KioskClinicBinding = {
    dbName: params.dbName.trim(),
    clinicId: selectedClinic.clinicId.trim(),
  }
  setKioskBinding(binding)
  setKioskDeviceMode()
  return {
    binding,
    accessToken: params.accessToken,
    actorId: params.actorId,
  }
}

/** Staff login used only to activate this device for a clinic. */
export async function activateKioskWithStaffLogin(params: {
  username: string
  password: string
}): Promise<KioskActivationOutcome> {
  const response = await getAuthToken(
    params.username.trim(),
    params.password,
    STAFF_LOGIN_SCOPE,
    { persistSession: false },
  )
  if (response.statusCode !== 200 || !response.data?.db_name?.trim()) {
    throw new Error(response.message || 'Unable to activate kiosk')
  }

  const clinicsResponse = await getAllClinics({
    accessToken: response.data.access_token,
  })
  const clinics = clinicsResponse.data?.clinics ?? []
  const storedClinicId = localStorage.getItem('clinic_id')?.trim() ?? ''
  if (clinics.length === 0) {
    throw new Error('No clinic is available for this account.')
  }
  if (clinics.length > 1) {
    return {
      requiresClinicSelection: true,
      dbName: response.data.db_name.trim(),
      accessToken: response.data.access_token,
      actorId: response.data.user_id,
      clinics: clinics.map((clinic) => ({
        clinicId: clinic.clinic_id,
        clinicName: clinic.clinic_name,
      })),
      suggestedClinicId: clinics.some(
        (clinic) => clinic.clinic_id === storedClinicId,
      )
        ? storedClinicId
        : clinics[0].clinic_id,
    }
  }

  return completeKioskActivation({
    dbName: response.data.db_name.trim(),
    accessToken: response.data.access_token,
    actorId: response.data.user_id,
    clinics: clinics.map((clinic) => ({
      clinicId: clinic.clinic_id,
      clinicName: clinic.clinic_name,
    })),
    clinicId: clinics[0].clinic_id,
  })
}
